import { useCallback, useEffect, useState } from 'react';
import type { CifrasReservas, EstadoReserva, Reserva } from '../../../api/ops/reservas';
import { claveIdempotencia } from '../../../api/operaciones';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Chip, type EstadoChip } from '../../../componentes/Chip';
import { Deshacer } from '../../../componentes/Deshacer';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { TarjetaCifra } from '../../../componentes/TarjetaCifra';
import { rolesDe } from '../../../logica/acceso';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { useCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { dondeEs, nombreDe, nombreRecursos, nombreTurno, nombreZona, useNombres, type ContextoReservas } from '../contexto';
import { diaLargo, diasEntre, etiquetaDia, hoyEn, horaEn } from '../fechas';
import { APAGADAS, AYUDA, ESTADO, FILTROS, PERMISOS, TARJETAS, cifraDeFiltro, conPalabra, personasDe, sumaDe } from '../textos';
import { HojaDelDia } from './HojaDelDia';
import { NavegadorDia } from './NavegadorDia';
import { useHorarioDelDia } from './horario';

/**
 * 8.1 Reservas del día: el día (RV Navegador) con «Ahora» si es hoy, el horario del día (G245) e «Imprimir» (G796),
 * las cuatro cifras de `counts` de listReservations con `service_date` (G797: Reservas, Por llegar, Presentadas y Por
 * completar, en negro si hay alguna), filtros por estado (con sus cifras de `counts`), zona y búsqueda, y la lista por
 * turno (ordenador) o por hora (móvil) con la baldosa de la hora, el nombre, «4 pers. · Terraza · Reservado T4» y el chip
 * solo cuando hay que actuar. «Ha llegado» de un toque con «Deshacer» 5 s (G159): la llegada sale a los 5 s con
 * `validatePass` (`method: manual`) sobre el pase activo de la reserva (D-047). Toda la fila abre la ficha (`?reserva=`).
 */
interface Props extends PropsPantalla {
    contexto: ContextoReservas;
    puedeCompletar: boolean;
    onPorCompletar: () => void;
}

interface Carga {
    dia: string;
    lista: Reserva[];
    cifras: CifrasReservas | null;
    error: unknown;
}

async function todasDelDia(ops: PropsPantalla['ops'], negocio: string, dia: string): Promise<{ lista: Reserva[]; cifras: CifrasReservas | null }> {
    const lista: Reserva[] = [];
    let cifras: CifrasReservas | null = null;
    let cursor: string | null = null;
    for (let i = 0; i < 10; i += 1) {
        const p = await ops.listReservations(negocio, { service_date: dia, sort: 'starts_at', limit: 100, cursor });
        if (i === 0) {
            cifras = p.counts ?? null;
        }
        lista.push(...p.data);
        cursor = p.next_cursor ?? null;
        if (cursor === null) {
            break;
        }
    }
    return { lista, cifras };
}

export function ReservasDelDia(props: Props) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, busqueda, ruta, contexto, puedeCompletar, onPorCompletar } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const hoy = hoyEn(contexto.zona);
    const [dia, setDia] = useState(() => {
        const d = busqueda.get('dia');
        return d !== null && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : hoy;
    });
    const [filtro, setFiltro] = useState(() => Math.max(0, FILTROS.findIndex((f) => f.clave === busqueda.get('filtro'))));
    const [zonaF, setZonaF] = useState('');
    const [buscar, setBuscar] = useState('');
    const [carga, setCarga] = useState<Carga | null>(null);
    const [intento, setIntento] = useState(0);
    const [locales, setLocales] = useState<Readonly<Record<string, EstadoReserva>>>({});
    const [imprimir, setImprimir] = useState(false);
    const horario = useHorarioDelDia(ops, negocio, dia, permisos.puede(PERMISOS.horario));
    const propio = permisos.ambito(PERMISOS.ver) === 'own';
    const puedeLlegada = permisos.puede(PERMISOS.llegada) && !soloLectura;

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        let vivo = true;
        todasDelDia(ops, negocio, dia)
            .then(({ lista, cifras }) => {
                if (vivo) {
                    setCarga({ dia, lista, cifras, error: null });
                    setLocales({});
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCarga((c) => ({ dia, lista: c?.dia === dia ? c.lista : [], cifras: c?.dia === dia ? c.cifras : null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, dia, intento]);

    const lista = carga?.dia === dia && carga.error === null ? carga.lista : null;
    const nombres = useNombres(ops, negocio, (lista ?? []).map((r) => r.customer_id).filter((x): x is string => typeof x === 'string'), permisos.puede(PERMISOS.clientes));

    // 8.2: «Nueva reserva» abre el panel lateral con `?nueva=1` (lo pinta Reservas.tsx sobre la lista).
    const rutaBase = ruta.split('?')[0] ?? ruta;
    useCabecera({
        ayuda: conPalabra(AYUDA.dia, contexto.palabra),
        acciones: permisos.puede(PERMISOS.crear) && !soloLectura ? [{ clave: 'nueva', texto: 'Nueva reserva', icono: 'calendar-plus', onClick: () => { navegar(`${rutaBase}?apartado=dia&dia=${dia}&nueva=1`); } }] : [],
    });

    const base = ruta.split('?')[0] ?? ruta;
    const abrir = (id: string) => {
        navegar(`${base}?apartado=dia&dia=${dia}&reserva=${encodeURIComponent(id)}`);
    };

    const haLlegado = (r: Reserva, nombre: string) => {
        setLocales((x) => ({ ...x, [r.id]: 'arrived' }));
        envio.programar({
            texto: `${nombre} ha llegado.`,
            donde: r.id,
            enviar: () => {
                ops.listPasses(negocio, { passable_type: 'reserva', passable_id: r.id, status: 'active', limit: 1 })
                    .then((p) => {
                        const pase = p.data[0];
                        if (pase === undefined) {
                            throw new Error('sin pase');
                        }
                        return ops.validatePass(negocio, { method: 'manual', pass_id: pase.id, client_scan_id: claveIdempotencia() });
                    })
                    .then((l) => {
                        if (l.result !== 'green') {
                            avisar({ tipo: 'error', texto: l.message ?? 'No se ha podido registrar la llegada.' });
                        }
                        recargar();
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido registrar la llegada.' });
                        recargar();
                    });
            },
            alDeshacer: () => {
                setLocales((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== r.id)));
            },
        });
    };

    if (carga?.dia === dia && carga.error !== null && carga.lista.length === 0) {
        const e = estadoDeError(carga.error);
        return e.tipo === 'sin-permiso' ? (
            <EstadoSinPermiso seccion="Reservas" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
        ) : (
            <EstadoError seccion="las reservas" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />
        );
    }

    const ahora = new Date();
    const estadoDe = (r: Reserva): EstadoReserva => locales[r.id] ?? r.status;
    // G844: desde cuándo se puede marcar «no vino» lo dice la API (inicio más el margen de la regla de reservas).
    const conRetraso = (r: Reserva) => estadoDe(r) === 'confirmed' && r.service_date === hoy && r.no_show_markable_from != null && ahora.getTime() >= new Date(r.no_show_markable_from).getTime();
    const cifras = carga?.dia === dia ? carga.cifras : null;
    const f = FILTROS[filtro] ?? FILTROS[0];
    const q = buscar.trim().toLocaleLowerCase('es');
    const visibles = (lista ?? []).filter(
        (r) =>
            (f?.estados == null || f.estados.includes(estadoDe(r))) &&
            (zonaF === '' || r.zone_id === zonaF) &&
            (q === '' || nombreDe(r, nombres).toLocaleLowerCase('es').includes(q) || r.reference.toLocaleLowerCase('es').includes(q)),
    );
    const hayFiltros = filtro !== 0 || zonaF !== '' || q !== '';

    const fila = (r: Reserva) => {
        const estado = estadoDe(r);
        const nombre = nombreDe(r, nombres);
        const pendiente = envio.pendiente?.donde === r.id;
        const apagada = APAGADAS.includes(estado) || estado === 'no_show';
        const sinRecurso = r.resources.length === 0 && !apagada;
        const chips: [string, EstadoChip][] = [];
        if (estado === 'pending_payment') {
            chips.push([ESTADO.pending_payment[0], 'pending']);
        }
        if (conRetraso(r)) {
            chips.push(['Con retraso', 'pending']);
        }
        if (estado === 'pending_completion') {
            chips.push(['Por completar', 'pending']);
        }
        if (sinRecurso) {
            chips.push([r.needs_relocation === true ? 'Para recolocar' : r.assignment_status === 'pending_join' ? 'Por juntar' : `Sin ${contexto.palabra}`, 'pending']);
        }
        const llegada = r.arrived_at == null ? null : horaEn(r.arrived_at, contexto.zona);
        const quieto = pendiente
            ? ''
            : estado === 'pending_payment' && r.payment_expires_at != null
              ? `Paga antes de las ${horaEn(r.payment_expires_at, contexto.zona)}`
              : (estado === 'arrived' || estado === 'completed') && llegada !== null
                ? `Llegó a las ${llegada}`
                : estado === 'completed'
                  ? 'Vino'
                  : estado === 'no_show'
                    ? 'No vino'
                    : APAGADAS.includes(estado)
                      ? ESTADO[estado][0]
                      : '';
        const llego = !pendiente && puedeLlegada && estado === 'confirmed' && r.service_date === hoy;
        const hora = r.local_time ?? horaEn(r.starts_at, contexto.zona);
        const detalle = [r.kind === 'walk_in' ? 'Walk-in' : '', `${String(r.party_size)} pers.`, nombreZona(contexto, r.zone_id), nombreRecursos(contexto, r)].filter((x) => x !== '').join(' · ');
        return { r, estado, nombre, pendiente, chips, quieto, llego, hora, detalle, apagada };
    };

    const filas = visibles.map(fila);
    // Ordenador: por turno de la zona (Comida · Cena), o por hora si no tiene; móvil: por hora.
    const grupos: { titulo: string; filas: ReturnType<typeof fila>[] }[] = [];
    for (const x of filas) {
        const titulo = movil ? x.hora : (nombreTurno(contexto, x.r) ?? x.hora);
        const g = grupos.find((y) => y.titulo === titulo);
        if (g === undefined) {
            grupos.push({ titulo, filas: [x] });
        } else {
            g.filas.push(x);
        }
    }
    const meta = (l: ReturnType<typeof fila>[]) => {
        const personas = l.filter((x) => !APAGADAS.includes(x.estado)).reduce((n, x) => n + x.r.party_size, 0);
        return `${String(l.length)} ${l.length === 1 ? 'reserva' : 'reservas'} · ${String(personas)} personas`;
    };

    const n = (estados: readonly EstadoReserva[]) => (cifras === null ? '—' : String(sumaDe(cifras, estados)));
    const nComp = cifras === null ? 0 : sumaDe(cifras, TARJETAS.porCompletar);
    const nLlegar = cifras === null ? 0 : sumaDe(cifras, TARJETAS.porLlegar);
    // G843: las personas de cada cifra las suma la API (`people_by_status`).
    const pie = (estados: readonly EstadoReserva[]) => {
        const p = cifras === null ? null : personasDe(cifras, estados);
        return p === null ? {} : { pie: `${String(p)} ${p === 1 ? 'persona' : 'personas'}` };
    };
    const verFiltro = (clave: string) => () => {
        setFiltro(Math.max(0, FILTROS.findIndex((x) => x.clave === clave)));
    };
    const relativo = diasEntre(dia, hoy);
    const cuando = relativo === 0 ? 'hoy' : relativo === -1 ? 'ayer' : relativo === 1 ? 'mañana' : `el ${diaLargo(dia)}`;
    const nota = propio
        ? 'Ves las reservas que has vendido tú. Crear reservas con tu cupo está pendiente de decidir.'
        : soloLectura
          ? 'El negocio está en solo lectura: puedes consultar las reservas, pero no crear, cambiar ni completar ninguna.'
          : null;

    return (
        <div className="rv-rs-apartado">
            <div className="rv-rs-dia">
                <NavegadorDia valor={dia} hoy={hoy} movil={movil} onCambio={(d) => { setDia(d); setCarga(null); }} />
                {dia === hoy && !movil && <Chip estado="live">{`Ahora, ${horaEn(ahora, contexto.zona)}`}</Chip>}
            </div>
            <div className="rv-rs-horario">
                <span className="rv-rs-horario__texto">
                    <Icono nombre="clock" />
                    <span>{horario === null ? '' : horario.texto(dia === hoy ? 'Hoy' : 'Ese día')}</span>
                </span>
                {!movil && !propio && (
                    <button
                        type="button"
                        className="rv-b rv-b-sec"
                        aria-label="Imprimir o guardar en PDF las reservas del día"
                        onClick={() => {
                            if (contexto.planoSala) {
                                navegar(`/sala?imprimir=1&dia=${dia}`);
                            } else {
                                setImprimir(true);
                            }
                        }}
                    >
                        <Icono nombre="printer" />
                        <span>Imprimir</span>
                    </button>
                )}
            </div>
            <div className={`rv-rs-cifras${movil ? ' rv-rs-cifras--movil' : ''}`}>
                <TarjetaCifra titulo="Reservas" cifra={n(TARJETAS.reservas)} {...pie(TARJETAS.reservas)} enlace={{ texto: 'Ver más', accion: verFiltro('todas') }} />
                <TarjetaCifra titulo="Por llegar" cifra={n(TARJETAS.porLlegar)} pie={nLlegar > 0 ? 'Confirmadas' : 'Nadie por llegar'} enlace={{ texto: 'Ver más', accion: verFiltro('por-llegar') }} />
                <TarjetaCifra titulo="Presentadas" cifra={n(TARJETAS.presentadas)} {...pie(TARJETAS.presentadas)} enlace={{ texto: 'Ver más', accion: verFiltro('presentadas') }} />
                <TarjetaCifra
                    titulo="Por completar"
                    cifra={n(TARJETAS.porCompletar)}
                    oscura={nComp > 0}
                    pie={nComp > 0 ? (puedeCompletar ? 'Indica si vinieron' : 'Las completa el equipo') : 'Todo al día'}
                    {...(nComp > 0 && puedeCompletar ? { enlace: { texto: 'Completar', accion: onPorCompletar } } : {})}
                />
            </div>
            {nota !== null && (
                <div className="rv-rs-nota">
                    <Icono nombre={propio ? 'eye' : 'lock'} />
                    <span>{nota}</span>
                </div>
            )}
            <div className="rv-rs-herramientas">
                <Filtros etiqueta="Estado de las reservas" items={FILTROS.map((x) => ({ etiqueta: x.etiqueta, cifra: cifraDeFiltro(cifras, x.estados) }))} valor={filtro} onCambio={setFiltro} />
                <details className="rv-pg-plegable rv-pg-plegable--plano rv-rs-ajustes">
                    <summary aria-label="Ver las reservas">
                        <Icono nombre="sliders-horizontal" />
                    </summary>
                    <div className={`rv-pg-ajustes${movil ? ' rv-pg-ajustes--movil' : ''}`}>
                        <Desplegable etiqueta="Zona" valor={zonaF} opciones={[{ valor: '', texto: 'Todas las zonas' }, ...contexto.zonas.map((z) => ({ valor: z.id, texto: z.name ?? '' }))]} onChange={setZonaF} />
                        <Campo etiqueta="Buscar" tipo="search" valor={buscar} onChange={setBuscar} ayuda="Por nombre o localizador." />
                    </div>
                </details>
            </div>
            {lista === null ? (
                <EstadoCargando seccion="las reservas" />
            ) : filas.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa"><Icono nombre="inbox" /></span>
                    <strong>{lista.length === 0 ? `No hay reservas ${cuando}.` : 'Ninguna reserva con estos filtros.'}</strong>
                    <span>{lista.length === 0 ? 'Cuando entre alguna, por la web, el asistente o el panel, la verás aquí.' : 'Prueba con otro estado, otra zona o quita la búsqueda.'}</span>
                    {lista.length > 0 && hayFiltros && (
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setFiltro(0); setZonaF(''); setBuscar(''); }}>
                            <Icono nombre="x" />
                            <span>Quitar filtros</span>
                        </button>
                    )}
                </div>
            ) : (
                <div role="list" aria-label="Reservas del día" className="rv-rs-grupos">
                    {grupos.map((g) => (
                        <div key={g.titulo} className="rv-rs-grupo">
                            <div className="rv-rs-grupo__cab">
                                <span className={movil ? 'rv-rs-grupo__hora' : 'rv-rs-ceja'}>{g.titulo}</span>
                                <span>{meta(g.filas)}</span>
                            </div>
                            {g.filas.map((x) =>
                                movil ? (
                                    <div key={x.r.id} role="listitem" className="rv-rs-tarjeta">
                                        <button type="button" className="rv-rs-tarjeta__abrir" style={{ opacity: x.apagada ? 0.6 : 1 }} onClick={() => { abrir(x.r.id); }}>
                                            <span className="rv-rs-fila__texto">
                                                <span className="rv-rs-tarjeta__cab">
                                                    <strong>{x.nombre}</strong>
                                                    <span className="rv-rs-tarjeta__pax"><Icono nombre="users" />{String(x.r.party_size)}</span>
                                                </span>
                                                <span>{[x.r.kind === 'walk_in' ? 'Walk-in' : '', dondeEs(contexto, x.r)].filter((t) => t !== '').join(' · ')}</span>
                                                {x.chips.length > 0 && <span className="rv-rs-chips">{x.chips.map(([t, e]) => <Chip key={t} estado={e}>{t}</Chip>)}</span>}
                                            </span>
                                            <Icono nombre="chevron-right" />
                                        </button>
                                        {x.llego && (
                                            <button type="button" className="rv-b rv-b-sec rv-b--movil rv-rs-ancha" onClick={() => { haLlegado(x.r, x.nombre); }}>
                                                <Icono nombre="log-in" />
                                                <span>Ha llegado</span>
                                            </button>
                                        )}
                                        {x.pendiente && envio.pendiente !== null && <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil ancho="100%" />}
                                        {!x.llego && !x.pendiente && x.quieto !== '' && <span className="rv-rs-pista">{x.quieto}</span>}
                                    </div>
                                ) : (
                                    <div key={x.r.id} role="listitem" className="rv-rs-fila">
                                        <button type="button" className="rv-rs-fila__abrir" aria-label={`Abrir la reserva de ${x.nombre}, ${x.hora}`} style={{ opacity: x.apagada ? 0.6 : 1 }} onClick={() => { abrir(x.r.id); }}>
                                            <span className="rv-rs-baldosa"><strong>{x.hora}</strong><small>{etiquetaDia(x.r.service_date, hoy)}</small></span>
                                            <span className="rv-rs-fila__texto">
                                                <strong>{x.nombre}</strong>
                                                <span>{x.detalle}</span>
                                            </span>
                                            {x.chips.length > 0 && <span className="rv-rs-chips">{x.chips.map(([t, e]) => <Chip key={t} estado={e}>{t}</Chip>)}</span>}
                                            {x.quieto !== '' && !x.llego && <span className="rv-rs-quieto">{x.quieto}</span>}
                                        </button>
                                        {x.llego && (
                                            <button type="button" className="rv-b rv-b-fan" aria-label={`${x.nombre} ha llegado`} onClick={() => { haLlegado(x.r, x.nombre); }}>
                                                <Icono nombre="log-in" />
                                                <span>Ha llegado</span>
                                            </button>
                                        )}
                                        {x.pendiente && envio.pendiente !== null && <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} />}
                                    </div>
                                ),
                            )}
                        </div>
                    ))}
                </div>
            )}
            {imprimir && lista !== null && (
                <HojaDelDia
                    dia={dia}
                    lista={lista}
                    nombres={nombres}
                    contexto={contexto}
                    quien={props.yo.account.display_name}
                    onCerrar={() => { setImprimir(false); }}
                />
            )}
        </div>
    );
}
